import { RiArrowRightSLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { IconChip, type IconChipProps } from './icon-chip';
import { tv, type VariantProps } from './lib/tv';

export const cardStyles = tv({
  base: 'flex min-w-0 flex-col',
  variants: {
    /** `accent` — settings card / table frame (559:17358): panel + 1px accent/20, r12. */
    variant: {
      accent: 'card-accent',
      panel: 'rounded-12 bg-panel',
      subtle: 'rounded-8 bg-field',
      outline: 'rounded-12 border border-line bg-panel',
    },
    padding: { none: 'p-0', sm: 'gap-10 p-12', md: 'gap-14 p-16' },
  },
  defaultVariants: { variant: 'accent', padding: 'md' },
});

export interface CardProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>, VariantProps<typeof cardStyles> {
  /** `t-card-title` (600 15/21). */
  title?: React.ReactNode;
  /** 400 12/17 White/60, 2 px under the title. */
  description?: React.ReactNode;
  /** Right side of the header (buttons, badges). */
  actions?: React.ReactNode;
  as?: 'section' | 'div' | 'article';
}

/** Settings card (spec §4.19 "Settings card" 559:17358): `card-accent` r12, padding 16, gap 14. */
export function Card({ title, description, actions, variant, padding, as: Tag = 'section', className, children, ...rest }: CardProps) {
  return (
    <Tag {...rest} className={cardStyles({ variant, padding, class: className })}>
      {(title || description || actions) && (
        <header className="flex items-start gap-12">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {title && <h3 className="t-card-title text-fg">{title}</h3>}
            {description && <p className="text-xs leading-17 tracking-normal text-fg-secondary">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-8">{actions}</div>}
        </header>
      )}
      {children}
    </Tag>
  );
}

export interface LinkCardProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  chip: Pick<IconChipProps, 'icon' | 'color'>;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
}

/** Link card (556:16443): `card-accent` 320×66, padding 12, gap 12: chip 40 r8 + title + subtitle + chevron. */
export function LinkCard({ chip, title, subtitle, className, type = 'button', ...rest }: LinkCardProps) {
  return (
    <button
      type={type}
      {...rest}
      className={cn(
        'card-accent flex w-320 items-center gap-12 p-12 text-left transition-colors duration-150 hover:bg-hover',
        className,
      )}
    >
      <IconChip {...chip} size={40} className="rounded-8" />
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="truncate text-sm font-semibold text-fg">{title}</span>
        {subtitle && <span className="truncate text-xs leading-17 tracking-normal text-fg-secondary">{subtitle}</span>}
      </span>
      <RiArrowRightSLine aria-hidden className="size-16 shrink-0 text-icon" />
    </button>
  );
}
